/**
 * CAB WebSocket Service
 *
 * Provides real-time WebSocket connection for CAB progress updates.
 * Follows the designWebsocketService.ts pattern (project-based connection).
 *
 * Events:
 * - cab_progress_update: Phase/step progress
 * - cab_phase_completed: A phase finished
 * - cab_section_ready: A section was generated
 * - cab_skeleton_ready: Skeleton available for preview
 * - cab_completed: Full generation complete
 * - cab_error: An error occurred
 */

import { getApiBaseUrl as getHttpApiBaseUrl } from '../utils/axios';
import type { CABWebSocketMessage, CABWebSocketEventType } from '../types/cab';

const getWsBaseUrl = () => {
  const httpApiBase = getHttpApiBaseUrl();
  const httpOrigin = httpApiBase.replace(/\/?api$/, '');
  return httpOrigin.replace(/^http/, 'ws');
};

export interface CABWebSocketCallbacks {
  onMessage?: (message: CABWebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  onReconnect?: () => void;
}

export class CABWebSocket {
  private ws: WebSocket | null = null;
  private projectId: string;
  private token: string;
  private callbacks: CABWebSocketCallbacks;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = Infinity;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 60000;
  private isConnecting = false;
  private shouldReconnect = true;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private pongTimeout: ReturnType<typeof setTimeout> | null = null;
  private hasConnectedBefore = false;
  private visibilityChangeListener: (() => void) | null = null;
  private networkStatusListener: (() => void) | null = null;

  constructor(projectId: string, token: string, callbacks: CABWebSocketCallbacks = {}) {
    this.projectId = projectId;
    this.token = token;
    this.callbacks = callbacks;
  }

  updateToken(token: string): void {
    this.token = token;
  }

  updateCallbacks(callbacks: CABWebSocketCallbacks): void {
    this.callbacks = callbacks;
  }

  async connect(): Promise<void> {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isConnecting = true;

    try {
      const wsBase = getWsBaseUrl();
      const url = `${wsBase}/api/cab/${this.projectId}/ws?token=${this.token}`;

      console.log(`🔌 CAB WS: Connecting to ${wsBase}/api/cab/${this.projectId}/ws`);
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;

        console.log(`🔌 CAB WS: Connected to ${this.projectId}`);

        if (this.hasConnectedBefore) {
          console.log('🔌 CAB WS: Reconnected');
          this.callbacks.onReconnect?.();
        }
        this.hasConnectedBefore = true;
        this.callbacks.onConnect?.();
        this.startPingInterval();
        this.setupListeners();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as CABWebSocketMessage;

          if (message.type === 'pong') {
            this.clearPongTimeout();
            return;
          }

          console.log(`🔌 CAB WS: Received [${message.type}]`, message.data);
          this.callbacks.onMessage?.(message);
        } catch (err) {
          console.warn('🔌 CAB WS: Invalid message', err);
        }
      };

      this.ws.onerror = (error) => {
        this.isConnecting = false;
        console.error('🔌 CAB WS: Error', error);
        this.callbacks.onError?.(error);
      };

      this.ws.onclose = (event) => {
        this.isConnecting = false;
        this.stopPingInterval();
        console.log(`🔌 CAB WS: Closed (code=${event.code}, reason=${event.reason || 'none'})`);
        this.callbacks.onDisconnect?.();

        if (this.shouldReconnect) {
          this.scheduleReconnect();
        }
      };
    } catch (err) {
      this.isConnecting = false;
      console.error('CAB WS: Connection failed', err);
    }
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.stopPingInterval();
    this.clearPongTimeout();
    this.removeListeners();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          type: 'ping',
          timestamp: new Date().toISOString(),
        }));

        // Set pong timeout
        this.pongTimeout = setTimeout(() => {
          console.warn('CAB WS: Pong timeout, reconnecting');
          this.ws?.close();
        }, 10000);
      }
    }, 30000);
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    this.clearPongTimeout();
  }

  private clearPongTimeout(): void {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;

    const delay = Math.min(
      this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts),
      this.maxReconnectDelay,
    );
    this.reconnectAttempts++;

    console.log(`🔌 CAB WS: Reconnecting in ${(delay / 1000).toFixed(1)}s (attempt ${this.reconnectAttempts})`);
    setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect();
      }
    }, delay);
  }

  private setupListeners(): void {
    // Visibility change — reconnect when tab becomes visible
    this.visibilityChangeListener = () => {
      if (!document.hidden && !this.isConnected() && this.shouldReconnect) {
        this.connect();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeListener);

    // Network online — reconnect
    this.networkStatusListener = () => {
      if (navigator.onLine && !this.isConnected() && this.shouldReconnect) {
        this.connect();
      }
    };
    window.addEventListener('online', this.networkStatusListener);
  }

  private removeListeners(): void {
    if (this.visibilityChangeListener) {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
      this.visibilityChangeListener = null;
    }
    if (this.networkStatusListener) {
      window.removeEventListener('online', this.networkStatusListener);
      this.networkStatusListener = null;
    }
  }
}

/**
 * Manager for CAB WebSocket connections (one per project).
 */
export class CABWebSocketManager {
  private connections: Map<string, CABWebSocket> = new Map();

  getOrCreate(projectId: string, token: string, callbacks: CABWebSocketCallbacks = {}): CABWebSocket {
    const existing = this.connections.get(projectId);
    if (existing) {
      existing.updateToken(token);
      existing.updateCallbacks(callbacks);
      return existing;
    }

    const ws = new CABWebSocket(projectId, token, callbacks);
    this.connections.set(projectId, ws);
    return ws;
  }

  disconnect(projectId: string): void {
    const ws = this.connections.get(projectId);
    if (ws) {
      ws.disconnect();
      this.connections.delete(projectId);
    }
  }

  disconnectAll(): void {
    this.connections.forEach((ws) => ws.disconnect());
    this.connections.clear();
  }
}

export const cabWebSocketManager = new CABWebSocketManager();
